import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

const AboutPage: React.FC = () => {
  useEffect(() => {
    // SEO meta tags
    document.title = 'Esin Arifoğlu Hakkında | ICF Akredite Koç | Ankara Koçluk';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'ICF akredite koçu Esin Arifoğlu hakkında bilgi. 25+ yıl eğitim deneyimi, Katı Hal Fiziği yüksek lisans, Ankara merkezli profesyonel koçluk hizmetleri.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Esin Arifoğlu, ICF koçu Ankara, profesyonel koç biyografi, koçluk deneyimi, Ankara koç');
    }

    // Schema.org JSON-LD for about page
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Esin Arifoğlu",
      "jobTitle": "ICF Akredite Profesyonel Koç",
      "description": "25+ yıl eğitim deneyimi olan ICF akredite koçu. Öğrenci, ebeveyn ve kariyer koçluğu alanlarında uzman.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/hakkimda`,
      "worksFor": {
        "@type": "Organization",
        "name": "SeninYolun Koçluk"
      },
      "alumniOf": {
        "@type": "EducationalOrganization",
        "name": "Katı Hal Fiziği Yüksek Lisans Programı"
      },
      "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "name": "ICF Akredite Koç Sertifikası",
          "credentialCategory": "Professional Certification"
        },
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Katı Hal Fiziği Yüksek Lisans",
          "credentialCategory": "Degree"
        }
      ],
      "knowsAbout": [
        "Öğrenci Koçluğu",
        "Ebeveyn Koçluğu", 
        "Kariyer Koçluğu",
        "Kişisel Gelişim",
        "Eğitim Danışmanlığı",
        "Fizik Eğitimi"
      ],
      "workLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Ankara",
          "addressRegion": "Çankaya",
          "addressCountry": "TR"
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      document.title = 'Ankara Öğrenci Ebeveyn Kariyer Koçluğu | Esin Arifoğlu ICF Koçu';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-teal-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  Merhaba, Ben <span className="text-teal-600">Esin Arifoğlu</span>
                </h1>
                <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                  ICF akredite koçu olarak, 25+ yıllık eğitim deneyimimi koçluk metodolojisiyle 
                  harmanlayarak size eşlik ediyorum.
                </p>
                <Button href="/ucretsiz-gorusme" size="lg">
                  Ücretsiz Ön Görüşme
                </Button>
              </div>
              <div>
                <img
                  src="https://static.readdy.ai/image/11bca27ccb3d56cff196f9e5dc04b8f8/2dcf212df82f4397e673ddeee3dccc68.jpeg"
                  alt="Esin Arifoğlu - ICF Akredite Koç"
                  className="rounded-2xl shadow-2xl w-full h-96 lg:h-[500px] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Biography Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Biyografim</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Merhaba, ben Esin Arifoğlu. 25+ yıldır eğitim alanında çalışıyor, son yıllarda ise 
                ICF akredite koçu olarak bireylerin potansiyellerini keşfetmelerine yardımcı oluyorum.
              </p>
              
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Katı Hal Fiziği alanında yüksek lisans eğitimim var ve Türkiye'nin önde gelen 
                okullarında fizik öğretmeni olarak görev yaptım. Bu deneyim, analitik düşünce 
                yapımı ve sistematik yaklaşımımı şekillendirdi.
              </p>
              
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Koçluk yolculuğuma başladığımda, bilimsel altyapımı insan gelişimi alanıyla 
                harmanlayarak benzersiz bir metodoloji geliştirdim. ICF standartlarına uygun 
                olarak sunduğum koçluk hizmetleri, öğrenci, ebeveyn ve kariyer koçluğu 
                alanlarında uzmanlaşmış durumda.
              </p>
            </div>
          </div>
        </section>

        {/* Credentials Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Sertifikalar ve Deneyim
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-award-line text-teal-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">ICF Akreditasyonu</h3>
                <p className="text-gray-700">
                  International Coaching Federation tarafından akredite edilmiş 
                  profesyonel koç sertifikası.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-graduation-cap-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Yüksek Lisans</h3>
                <p className="text-gray-700">
                  Katı Hal Fiziği alanında yüksek lisans derecesi. 
                  Analitik düşünce ve sistematik yaklaşım.
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-time-line text-purple-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">25+ Yıl Deneyim</h3>
                <p className="text-gray-700">
                  Türkiye'nin önde gelen okullarında fizik öğretmenliği 
                  ve eğitim danışmanlığı deneyimi.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Koçluk Felsefem
            </h2>
            
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8">
              <blockquote className="text-xl text-gray-800 italic text-center mb-6">
                "Her birey, kendi potansiyelini keşfetme ve hedeflerine ulaşma kapasitesine sahiptir. 
                Koç olarak rolüm, bu potansiyeli ortaya çıkarmak için gerekli ortamı sağlamaktır."
              </blockquote>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Bilimsel Yaklaşım</h3>
                  <p className="text-gray-700">
                    Fizik altyapım sayesinde, koçluk süreçlerinde sistematik ve 
                    analitik yaklaşım benimserim. Her durum için ölçülebilir 
                    hedefler belirler ve ilerlemeyi takip ederim.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">İnsan Odaklı</h3>
                  <p className="text-gray-700">
                    25+ yıllık eğitim deneyimim, bireylerin benzersizliğini 
                    anlamamı sağladı. Her koçluk süreci, kişiye özel olarak 
                    tasarlanır ve uygulanır.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold mb-6">Misyonum</h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Bireylerin kendi potansiyellerini keşfetmelerine, hedeflerini 
                  netleştirmelerine ve bu hedeflere ulaşmak için gerekli adımları 
                  atmalarına yardımcı olmak. ICF standartlarına uygun, bilimsel 
                  temelli koçluk hizmetleri sunarak yaşam kalitelerini artırmak.
                </p>
              </div>
              
              <div>
                <h2 className="text-3xl font-bold mb-6">Vizyonum</h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Türkiye'de koçluk alanında öncü bir isim olarak, öğrenci, 
                  ebeveyn ve kariyer koçluğu alanlarında standartları yükseltmek. 
                  Bilimsel yaklaşım ve insan odaklı metodolojimle, koçluk 
                  hizmetlerinin toplumsal faydasını artırmak.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Ethics Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Koçluk Etiğim
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-teal-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-teal-800 mb-4">Gizlilik</h3>
                <p className="text-teal-700">
                  Tüm koçluk seansları tamamen gizlidir. Paylaştığınız bilgiler 
                  kesinlikle üçüncü kişilerle paylaşılmaz.
                </p>
              </div>
              
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-blue-800 mb-4">Profesyonellik</h3>
                <p className="text-blue-700">
                  ICF etik kurallarına tam uyum göstererek, en yüksek 
                  profesyonel standartları koruyorum.
                </p>
              </div>
              
              <div className="bg-purple-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-purple-800 mb-4">Saygı</h3>
                <p className="text-purple-700">
                  Her bireyin değerlerine, inançlarına ve kararlarına 
                  saygı gösterir, yargılamadan destek sağlarım.
                </p>
              </div>
              
              <div className="bg-green-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-green-800 mb-4">Sürekli Gelişim</h3>
                <p className="text-green-700">
                  Koçluk becerilerimi sürekli geliştiriyor, güncel 
                  metodolojileri takip ediyorum.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;